import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, authHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, AlertTriangle, ArrowLeft, Heart } from "lucide-react";
import { toast } from "sonner";

const CONFIRM_PHRASE = "ELIMINA DEFINITIVAMENTE";

export const DeleteAccountSection = ({ role }) => {
  const navigate = useNavigate();
  const { clientLogout, vendorLogout, client, vendor } = useAuth();
  const accountEmail = ((role === "vendor" ? vendor?.email : client?.email) || "").trim().toLowerCase();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [confirm, setConfirm] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);

  const endpoint = role === "vendor" ? "/auth/account" : "/client/auth/account";
  const phraseOk = confirm.trim().toUpperCase() === CONFIRM_PHRASE;
  const emailOk = accountEmail.length > 0 && emailConfirm.trim().toLowerCase() === accountEmail;
  const canDelete = reason.trim().length > 0 && ack && phraseOk && emailOk;

  const reset = () => { setStep(1); setConfirm(""); setEmailConfirm(""); setReason(""); setAck(false); };
  const start = () => { reset(); setOpen(true); };
  const close = () => { setOpen(false); };

  const doDelete = async () => {
    if (!canDelete) return;
    setLoading(true);
    try {
      await api.delete(endpoint, { headers: authHeaders(role), data: { reason: reason.trim() } });
      toast.success("Account eliminato");
      if (role === "vendor") { vendorLogout(); navigate("/vendor/login"); }
      else { clientLogout(); navigate("/"); }
    } catch (_) {
      toast.error("Eliminazione non riuscita, riprova");
    } finally { setLoading(false); setOpen(false); }
  };

  const lostItems = role === "vendor"
    ? ["il tuo negozio e la vetrina", "tutte le offerte pubblicate", "le recensioni e i follower"]
    : ["i tuoi coupon richiesti e salvati", "i negozi che segui e i preferiti", "la cronologia e i vantaggi accumulati"];

  const stayMessage = role === "vendor"
    ? "Eliminare l'account non ti conviene davvero. KoupoNow è e resta completamente gratuito: tenere la tua vetrina attiva non ti costa nulla. E col tempo diventa sempre più utile, perché ogni settimana si iscrivono nuovi clienti nella tua zona, pronti a scoprire le tue offerte. Restando attivo continui a farti trovare e a ricevere nuovi contatti, senza dover rifare tutto da capo."
    : "Eliminare l'account non ti conviene davvero. KoupoNow è e resta completamente gratuito: tenerlo non ti costa nulla. E col tempo diventa sempre più conveniente, perché di continuo si aggiungono nuovi negozi, nuovi clienti e nuove offerte nella tua zona. Se oggi non trovi molto, lasciando l'account attivo sarai tra i primi a ricevere le prossime promozioni, senza dover rifare la registrazione.";

  return (
    <>
      {/* Discreet entry point — intentionally low-emphasis */}
      <div className="pt-1 text-center" data-testid="danger-zone">
        <button
          onClick={start}
          data-testid="delete-account-open"
          className="text-[11px] text-slate-300 hover:text-slate-400 underline underline-offset-2 transition-colors"
        >
          Elimina account
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="delete-account-dialog">
          {/* STEP 1 — Awareness, nudge to stay */}
          {step === 1 && (
            <div className="space-y-4">
              <DialogHeader>
                <div className="mx-auto h-12 w-12 rounded-2xl bg-amber-50 flex items-center justify-center mb-1">
                  <Heart className="h-6 w-6 text-amber-500" />
                </div>
                <DialogTitle className="text-xl font-extrabold text-center">Ci dispiace vederti andare via</DialogTitle>
              </DialogHeader>

              <div className="rounded-2xl bg-[#FFF7ED] border border-amber-100 p-3.5" data-testid="delete-stay-message">
                <p className="text-[13px] leading-relaxed text-amber-900">{stayMessage}</p>
              </div>

              <p className="text-sm text-slate-600 text-center">
                Eliminando l'account perderai <b>in modo definitivo</b>:
              </p>
              <ul className="space-y-1.5 text-sm text-slate-600 bg-slate-50 rounded-2xl p-3">
                {lostItems.map((t, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-slate-400 mt-0.5">•</span><span>{t}</span></li>
                ))}
              </ul>
              <p className="text-xs text-slate-400 text-center">
                Forse possiamo aiutarti: puoi disattivare le notifiche o cambiare le preferenze senza perdere nulla.
              </p>
              <div className="flex flex-col gap-2 pt-1">
                <Button onClick={close} data-testid="delete-keep-account" className="w-full h-12 rounded-xl font-bold text-base">
                  Mantieni il mio account
                </Button>
                <button
                  onClick={() => setStep(2)}
                  data-testid="delete-step1-continue"
                  className="w-full text-[12px] text-slate-400 hover:text-slate-500 underline underline-offset-2 py-1"
                >
                  Voglio comunque continuare con l'eliminazione
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — Reason */}
          {step === 2 && (
            <div className="space-y-4">
              <DialogHeader>
                <DialogTitle className="text-lg font-extrabold">Perché vuoi andartene?</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-500">Aiutaci a capire il motivo. È obbligatorio per proseguire.</p>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="delete-account-reason"
                className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
              >
                <option value="">Seleziona un motivo…</option>
                <option value="Non uso più l'app">Non uso più l'app</option>
                <option value="Poche offerte nella mia zona">Poche offerte nella mia zona</option>
                <option value="Troppe notifiche/email">Troppe notifiche/email</option>
                <option value="Problemi di privacy">Problemi di privacy</option>
                <option value="Ho un altro account">Ho un altro account</option>
                <option value="Altro">Altro</option>
              </select>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="delete-step2-back" className="flex-1 h-11 rounded-xl font-bold gap-1">
                  <ArrowLeft className="h-4 w-4" /> Indietro
                </Button>
                <Button onClick={() => setStep(3)} disabled={!reason} data-testid="delete-step2-continue" className="flex-1 h-11 rounded-xl font-bold">
                  Continua
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3 — Final friction: acknowledge + email + phrase */}
          {step === 3 && (
            <div className="space-y-4">
              <DialogHeader>
                <div className="mx-auto h-12 w-12 rounded-2xl bg-red-50 flex items-center justify-center mb-1">
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                </div>
                <DialogTitle className="text-lg font-extrabold text-center">Ultima conferma</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-600 text-center">Questa azione è <b>irreversibile</b> e non può essere annullata.</p>

              <label className="flex items-start gap-2.5 cursor-pointer select-none bg-slate-50 rounded-2xl p-3">
                <input
                  type="checkbox"
                  data-testid="delete-ack"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-[#DC2626]"
                />
                <span className="text-[13px] leading-snug text-slate-600">
                  Capisco che tutti i miei dati verranno eliminati definitivamente e che non potrò recuperarli.
                </span>
              </label>

              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-slate-600">Digita la tua email per confermare la tua identità</p>
                <Input
                  value={emailConfirm}
                  onChange={(e) => setEmailConfirm(e.target.value)}
                  placeholder={accountEmail || "la-tua-email@esempio.it"}
                  data-testid="delete-email-confirm"
                  className={`h-11 rounded-xl ${emailConfirm && !emailOk ? "border-red-300" : ""}`}
                />
              </div>

              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-slate-600">Digita esattamente <b className="text-red-600">{CONFIRM_PHRASE}</b></p>
                <Input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  data-testid="delete-account-confirm"
                  className={`h-11 rounded-xl ${confirm && !phraseOk ? "border-red-300" : ""}`}
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setStep(2)} data-testid="delete-step3-back" className="flex-1 h-11 rounded-xl font-bold gap-1">
                  <ArrowLeft className="h-4 w-4" /> Indietro
                </Button>
                <Button
                  onClick={doDelete}
                  disabled={loading || !canDelete}
                  data-testid="delete-account-confirm-btn"
                  className="flex-1 h-11 rounded-xl font-bold bg-[#DC2626] hover:bg-[#b91c1c] disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Elimina per sempre"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
